import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash';
import { ITradeProduct } from '../../../interfaces/trades/trade-product.model';
import { ITradeModel } from '../../../interfaces/trades/trade.model';
import { IRadioButton } from '../../../shared/components/radio-button/radio-button.model';
import { EditTradeProductsFormComponent } from '../../../shared/forms/trades/edit-trade-products-form/edit-trade-products-form.component';
import { ISelectList } from '../../../shared/models/select-list.model';
import { LookupsService } from '../../../shared/services/lookups/lookups.service';
import { MessageService } from '../../../shared/services/message/message.service';
import { ProductService } from '../../../shared/services/products/product.service';
import { TradeProductsService } from '../../../shared/services/trades/trade-product.service';
import { UserService } from '../../../shared/services/user/user.service';
import { ProductUtil } from '../../../utils/product';
import { GatekeeperPickerModalComponent } from '../../picker-modal.component';

@Component({
  selector: 'gk-edit-products-modal',
  templateUrl: './trade-products-picker.html'
})
export class TradeProductsPickerComponent
  extends GatekeeperPickerModalComponent<TradeProductsPickerComponent>
  implements OnInit, AfterViewInit
{
  @ViewChild(EditTradeProductsFormComponent, { static: true })
  tradeProductsForm: EditTradeProductsFormComponent;
  productUtil = ProductUtil;
  productSelector = [-2];
  filterText: string;
  trade: ITradeModel;
  isPack: number;
  radioButtons: Array<IRadioButton> = [];
  products: Array<ITradeProduct> = [];
  selectedProducts: Array<ITradeProduct> = [];
  productAdded: ITradeProduct;
  productFilterOptions: Array<ISelectList> = [];
  showAll = false;

  constructor(
    private tradeProductsService: TradeProductsService,
    private productService: ProductService,
    private messageService: MessageService,
    private userService: UserService,
    private lookupsService: LookupsService,
    public dialogRef: MatDialogRef<TradeProductsPickerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();
  }

  ngAfterViewInit() {
    this.radioButtons = [
      { name: 'Packs', value: 0, checked: this.isPack === 0 },
      { name: 'Units', value: 1, checked: this.isPack === 1 }
    ];
  }

  ngOnInit() {
    this.trade = this.data.locals.trade;
    this.selectedProducts = this.data.locals.selectedProducts;
    if (this.selectedProducts.length < 1) {
      this.isPack = this.userService.user.filters.lastStockUnitType;
    } else {
      this.isPack = this.trade.isPack ? 0 : 1;
    }

    this.lookupsService.productTypeFilters$.subscribe(pf => {
      this.productFilterOptions = pf;
      const existingOption = _.find(
        this.productFilterOptions,
        (o: ISelectList) => {
          return o.id === this.userService.user.filters.productSelector[0];
        }
      );
      if (!existingOption) return;

      this.userService.user.filters.productSelector.forEach(x => {
        this.productFilterOptions.find(y => y.id === x).selected = true;
      });

      return this._getProducts().then(() => {
        if (this.data.locals.addedProductId)
          this.tradeProductsService
            .getProduct(this.data.locals.addedProductId, this.trade.header)
            .then(p => this.addProduct(p));
      });
    });
  }

  onChangeUnitType() {
    this.selectedProducts.forEach((product: ITradeProduct) => {
      if (this.isPack === 0) {
        product.quantity = null;
        product.priceEach = null;
        product.packSize = product.previousPackSize;
      } else {
        product.packSize = null;
        product.packQuantity = null;
        product.packPrice = null;
      }
    });
  }

  removeProduct(productId: number) {
    const pr = _.find(
      this.products,
      (product: ITradeProduct) => product.productId === productId
    );
    pr.selected = false;
  }

  saveTradeProducts() {
    this.userService.updateUserSingleFilterPreference(
      'lastStockUnitType',
      this.isPack
    );
    return this.tradeProductsService
      .saveTradeProducts(
        this.trade.header,
        this.selectedProducts,
        this.isPack === 0 ? true : false
      )
      .then((r: Array<ITradeProduct>) =>
        this.close({ products: r, isPack: this.isPack === 0 })
      )
      .catch(() =>
        this.messageService.showMessage(
          MessageTypeEnum.Danger,
          'TRADE_PRODUCTS_SAVE_ERROR'
        )
      );
  }

  cleanProduct(p: ITradeProduct) {
    // removes any previous values
    p.previousPackPrice = null;
    p.previousPackQuantity = null;
    p.previousPriceEach = null;
    p.previousQuantity = null;
    p.packPrice = null;
    p.packQuantity = null;
    p.quantity = null;
    p.priceEach = null;

    if (p.previousPackSize !== 0 && p.previousPackSize !== null) {
      p.packSize = p.previousPackSize;
    } else {
      p.packSize = null;
      p.previousPackSize = null;
    }
  }

  addProduct(product: ITradeProduct) {
    const p = _.find(this.selectedProducts, (sp: ITradeProduct) => {
      return sp.productId === product.productId;
    });
    if (!p) {
      if (product.isExpanded) product.isExpanded = false;
      this.cleanProduct(product);
      this.selectedProducts.push(product);
      product.selected = true;
      this.tradeProductsForm.productAdded(product);
    }
  }

  filterChanged(productFilter: any) {
    this.filterText = productFilter.filterText;
    this.showAll =
      productFilter.showAll == null ? this.showAll : productFilter.showAll;
    this._getProducts();
  }

  setIsPack() {
    this.isPack = this.isPack === 0 ? 1 : 0;
  }

  isTradeProductsFormInvalid() {
    return (
      this.selectedProducts.length > 0 &&
      this.tradeProductsForm.theForm &&
      this.tradeProductsForm.theForm.invalid
    );
  }

  private _setSelectedProducts() {
    this.selectedProducts.forEach((sp: ITradeProduct) => {
      const pr = _.find(
        this.products,
        (p: ITradeProduct) => p.productId === sp.productId
      );

      if (pr) {
        pr.selected = true;
        this.addProduct(pr);
      }
    });
  }

  private _getProducts() {
    return this.tradeProductsService
      .getProducts(
        this.trade.header,
        this.filterText,
        this.productFilterOptions.filter(x => x.selected).map(x => x.id),
        this.showAll
      )
      .then((response: Array<ITradeProduct>) => {
        this.products = response;

        if (this.products.some(x => x.hasHiddenProducts)) {
          this.productService.hasHiddenProducts = true;
        } else {
          this.productService.hasHiddenProducts = false;
        }
      })
      .then(() => this._setSelectedProducts())
      .catch(() =>
        this.messageService.showMessage(
          MessageTypeEnum.Danger,
          'TRADE_PRODUCTS_GET_ERROR'
        )
      );
  }
}
